import { getFileIcon } from '@sohanscript/lib/code/get-file-icon';
import { stripDiffPrefixes } from '@sohanscript/lib/code/highlight';
import { parseFenceMeta } from '@sohanscript/markdown/fence-meta';
import type { ReactNode } from 'react';

type ResolveCodeBlockMetaInput = {
	childProps: Record<string, unknown>;
	childLanguage?: string;
	dataLang?: string;
	dataMeta?: string;
	dataCodeTitle?: string;
	dataFilename?: string;
	dataIcon?: string;
	dataName?: string;
};

export type CodeBlockMeta = {
	code: string;
	cleanCode: string;
	language: string;
	filename: string;
	icon: string;
	highlight: string;
	name?: string;
};

// NOTE: Keep parser metadata and hand-written code props on one precedence path.
export function resolveCodeBlockMeta({
	childProps,
	childLanguage,
	dataLang,
	dataMeta,
	dataCodeTitle,
	dataFilename,
	dataIcon,
	dataName,
}: ResolveCodeBlockMetaInput): CodeBlockMeta {
	const childClassLanguage = /language-([\w#+-]+)/.exec(
		String(childProps.className ?? ''),
	)?.[1];
	const childMeta = parseFenceMeta(
		childProps as Record<string, string | undefined>,
	);
	const ownMeta = parseFenceMeta({
		'data-lang': dataLang,
		'data-meta': dataMeta,
		'data-code-title': dataCodeTitle,
		'data-filename': dataFilename,
		'data-icon': dataIcon,
	});
	const language =
		ownMeta.language ||
		childMeta.language ||
		childClassLanguage ||
		childLanguage ||
		'txt';
	const code = String((childProps.children as ReactNode) ?? '').replace(
		/\n$/,
		'',
	);
	const filename = childMeta.filename || ownMeta.filename;
	const highlight = childMeta.highlight || ownMeta.highlight;

	return {
		code,
		cleanCode: stripDiffPrefixes(code),
		language,
		filename,
		icon: childMeta.icon || ownMeta.icon || getFileIcon({ language, filename }),
		highlight,
		name: childMeta.name || ownMeta.name || dataName,
	};
}
